import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Mail, Phone, MessageSquare, Loader2 } from "lucide-react";

const STATUS_META = {
  nuevo: { label: "Nuevo", cls: "bg-brand-aqua-soft text-brand-aqua-dark border-brand-aqua/40" },
  contactado: { label: "Contactado", cls: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/40" },
  descartado: { label: "Descartado", cls: "bg-stone-100 text-stone-500 border-stone-300" },
};

function fmt(iso) {
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function Interesados() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/leads"); setLeads(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/leads/${lead.id}`, { status });
      toast.success("Estado actualizado");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="interesados-page">
      <div>
        <div className="h-1 w-16 bg-brand-aqua mb-4 rounded-full" />
        <h1 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold">Interesados</h1>
        <p className="text-stone-600 mt-2 max-w-2xl"><span>Solicitudes recibidas desde la landing pública.</span></p>
      </div>

      {loading ? (
        <div className="card-elevated p-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
      ) : leads.length === 0 ? (
        <div className="card-elevated p-16 text-center text-stone-500"><Users className="h-8 w-8 mx-auto mb-2" /><span>Sin leads todavía.</span></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {leads.map((l) => {
            const st = STATUS_META[l.status] || STATUS_META.nuevo;
            return (
              <div key={l.id} className="card-elevated p-5 space-y-2" data-testid={`lead-${l.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-semibold text-brand-ink"><span>{l.name}</span></div>
                    <div className="text-xs text-stone-500"><span>{fmt(l.created_at)}</span></div>
                  </div>
                  <Badge className={`border font-semibold text-[10px] ${st.cls}`}><span>{st.label}</span></Badge>
                </div>
                <div className="text-sm text-stone-700 space-y-1">
                  <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-stone-400" /><a href={`mailto:${l.email}`} className="hover:text-brand-emerald-dark"><span>{l.email}</span></a></div>
                  {l.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-stone-400" /><a href={`tel:${l.phone}`} className="hover:text-brand-emerald-dark"><span>{l.phone}</span></a></div>}
                  {l.message && <div className="flex items-start gap-2 mt-2 text-xs text-stone-600 bg-stone-50 rounded p-2"><MessageSquare className="h-3.5 w-3.5 text-stone-400 shrink-0 mt-0.5" /><span>{l.message}</span></div>}
                </div>
                <div className="flex gap-2 pt-2 border-t border-border">
                  <Button size="sm" variant="outline" onClick={() => setStatus(l, "contactado")} data-testid={`lead-contactado-${l.id}`}><span>Contactado</span></Button>
                  <Button size="sm" variant="outline" onClick={() => setStatus(l, "descartado")} data-testid={`lead-descartado-${l.id}`}><span>Descartar</span></Button>
                  {l.status !== "nuevo" && <Button size="sm" variant="ghost" onClick={() => setStatus(l, "nuevo")}><span>Marcar nuevo</span></Button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
